<script setup lang="ts">
import type {
  PrimaryFeeling,
  SecondaryFeeling,
} from '@/ts/types/dto/interventions-dto';
import type {UuidDTO} from '@/ts/types/dto/sign-in-dto';
import {
  computed,
  ref,
} from 'vue';
import FormRequired from '@/vue/atoms/form-required.vue';
import FeelingSelection from '@/vue/molecules/components/feeling-selection.vue';
import FeelingChip from '@/vue/molecules/feeling-chip.vue';
import FlatIconBtn from '@/vue/molecules/flat-icon-btn.vue';
import FeelingsPopup from '@/vue/molecules/components/helpers/feelings-popup.vue';
import CardWithDetachedHeadline from '@/vue/molecules/components/helpers/card-with-detached-headline.vue';

const props = defineProps<{
  feelings: PrimaryFeeling[];
  descriptionHtml?: string;
}>();

defineEmits<{
  add: [],
}>();

const primaryModel = defineModel<UuidDTO[]>('primary');
const secondaryModel = defineModel<UuidDTO[]>('secondary');

const open = ref(false);

const values = computed<UuidDTO[]>({
  get() {
    return [...primaryModel.value!, ...secondaryModel.value!] as UuidDTO[];
  },
  set(value: UuidDTO[]) {
    primaryModel.value = value.filter((id) => props.feelings.some((feeling) => feeling.id === id));
    secondaryModel.value = value.filter((id) => !props.feelings.some((feeling) => feeling.id === id));
  },
});

const feelingsMap = computed(() => {
  const all: Record<UuidDTO, string> = {};
  props.feelings.forEach((feeling) => {
    all[feeling.id] = feeling.name;
    feeling.secondaryFeelings.forEach((secondaryFeeling: SecondaryFeeling) => {
      all[secondaryFeeling.id] = secondaryFeeling.name;
    });
  });
  return all;
});

function removeItem(val: UuidDTO): void {
  if (primaryModel.value!.includes(val)) {
    primaryModel.value?.splice(primaryModel.value!.indexOf(val), 1);
  } else {
    secondaryModel.value?.splice(secondaryModel.value!.indexOf(val), 1);
  }
}

</script>
<template>
  <card-with-detached-headline :title="$t('feelings')">
    <div v-if="descriptionHtml" v-safe-html="descriptionHtml" class="description"/>
    <feeling-selection v-model:feeling-selection="values" :feeling-options="feelings"/>

    <div class="feelings">
      <div class="feelings-list" role="list">
        <feeling-chip
          v-for="value in values"
          :key="value"
          class="feeling-chip"
          :name="feelingsMap[value]"
          role="listitem"
          is-selected
          enable-remove
          @remove="removeItem(value)"
        />
      </div>

      <flat-icon-btn
        icon="plus.svg"
        :label="$t('input_your_feelings_add')"
        @click="open = true"
      />
    </div>

    <form-required id="feelings-block" :valid="values.length > 0" :message="$t('select_one_option')"/>

    <feelings-popup v-model:ids="values" v-model:is-open="open" :feelings="feelings"/>
  </card-with-detached-headline>
</template>
<style scoped lang="scss">
.description {
  padding: var(--Spacing-spacing-4) var(--Spacing-spacing-6);
  padding-bottom: 0;
}

.feelings {
  display: flex;
  flex-shrink: 0;
  flex-wrap: wrap;
  gap: var(--Spacing-spacing-2);
  align-items: center;
  padding: var(--Spacing-spacing-1) var(--Spacing-spacing-6);
}

.feelings-list {
  display: contents;
}
</style>
